import { Typography } from '@fox/atoms';
import loget from 'lodash/get';
import * as React from 'react';
import styled from 'styled-components';
import { FoxColumnType } from '../../contracts/table';
import type { TableStyleProps } from '../data-grid';
import { Cell } from './common';

interface CellItemPreProps<T> {
	rowIndex: number;
	record: T;
	columnIndex: number;
	columnCount: number;
	columnType: FoxColumnType<T>;
	rowHeight?: string;
	tableStyle?: TableStyleProps;
}
const CellContents = styled(Typography)`
	text-overflow: ellipsis;
	overflow: hidden;
	margin: 0;
	font-size: 1.05em;
	font-weight: ${props => props?.theme?.regular ?? 'regular'};
	color: ${props => props?.theme?.colors?.font ?? 'inherit'};
`;
export const OverflowTextCell: React.FC<{ children: string }> = ({ children }) => {
	return <CellContents title={children}>{children}</CellContents>;
};

export const ClickableCell = ({
	onClick,
	children
}: React.PropsWithChildren<{ onClick: () => void; children: React.ReactNode }>) => {
	return (
		<div
			style={{ cursor: 'pointer' }}
			onClick={onClick}
		>
			{children}
		</div>
	);
};

export const calculateCellBgColours = (tableStyle: TableStyleProps | undefined, rowIndex: number): string | undefined => {
	if (!tableStyle || Object.keys(tableStyle).length === 0) return undefined;
	const colAlternateColours = Object.keys(tableStyle?.alternateCellShades ?? {}).length > 0 ? tableStyle?.alternateCellShades : undefined;
	const alternatingBgColor = colAlternateColours
		? rowIndex % 2 === 0
			? colAlternateColours.primary
			: colAlternateColours?.secondary
		: tableStyle?.cellsBgColour;

	return alternatingBgColor;
};

export const CellItemPre: React.FC<CellItemPreProps<Record<string, any>>> = ({
	rowIndex,
	columnType,
	columnIndex,
	columnCount,
	record,
	rowHeight,
	tableStyle
}) => {
	const bgColour = calculateCellBgColours(tableStyle, rowIndex);
	return (
		<Cell
			key={`${columnType.dataIndex}${rowIndex}${rowIndex}`}
			tabIndex={columnIndex + rowIndex * columnCount + 1}
			$rowHeight={rowHeight}
			$padCell={columnType.padCell}
			$bgColour={bgColour}
		>
			{columnType?.render ? (
				columnType.render(loget(record, columnType.dataIndex), record, columnIndex)
			) : columnType?.dataIndex ? (
				<OverflowTextCell>{loget(record, columnType.dataIndex)}</OverflowTextCell>
			) : (
				''
			)}
		</Cell>
	);
};

export const CellItem = React.memo(CellItemPre, (a, b) => {
	return JSON.stringify(a.record) === JSON.stringify(b.record) && a.columnType.dataIndex === b.columnType.dataIndex;
});
